class PreferredLocation extends HTMLElement {
  connectedCallback() {
    this._abort = new AbortController();
    this._cartAbort = null;
    this._cartSeq = 0;

    this.loadPreferredLocation();
    this.bindEvents();
    this.bindOutsideClick();
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._cartAbort?.abort();
    this._abort = null;
    this._cartAbort = null;
  }

  get cards() {
    return Array.from(this.querySelectorAll(".js-location-card"));
  }

  bindEvents() {
    this.addEventListener(
      "click",
      (e) => {
        const btn = e.target.closest(".js-set-preferred-btn");
        if (!btn || !this.contains(btn)) return;

        const card = btn.closest(".js-location-card");
        if (card) this.setPreferredLocation(card);
      },
      { signal: this._abort.signal },
    );
  }

  bindOutsideClick() {
    const details = this.closest("details");
    if (!details) return;

    // Capture phase so this fires before the summary's own bubble-phase toggle — otherwise the click that opens the dropdown also closes it.
    document.addEventListener(
      "click",
      (e) => {
        if (!details.open) return;
        if (details.contains(e.target)) return;
        details.querySelector("summary")?.click();
      },
      { signal: this._abort.signal, capture: true },
    );
  }

  loadPreferredLocation() {
    const initialLocation = this.dataset.initialLocation;
    if (!initialLocation) return;

    const safeId = CSS.escape(initialLocation);
    const card = this.querySelector(`[data-location-id="${safeId}"]`);
    if (card) {
      this.markAsPreferred(card, false);
    }
  }

  setPreferredLocation(card) {
    const locationId = card?.dataset?.locationId;
    if (!locationId) return;

    this.markAsPreferred(card, true);
  }

  async updateCartAttributes(locationId, locationName) {
    this._cartAbort?.abort();
    this._cartAbort = new AbortController();
    const seq = ++this._cartSeq;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: this._cartAbort.signal,
        body: JSON.stringify({
          attributes: {
            preferred_location_id: locationId,
            preferred_location_name: locationName || "",
          },
        }),
      });

      if (seq !== this._cartSeq) return;

      if (!response.ok) {
        console.error("[PreferredLocation] Cart update failed:", response.status);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("[PreferredLocation] Cart update error:", error);
      }
    }
  }

  markAsPreferred(card, syncToCart = true) {
    const locationId = card.dataset.locationId || "";
    const locationName = card.dataset.locationName || "";

    this.cards.forEach((c) => c.classList.remove("is-preferred"));
    card.classList.add("is-preferred");

    document.querySelectorAll(".js-header-locations").forEach((el) => {
      el.classList.add("has-location");
    });

    if (locationName) {
      document.querySelectorAll(".js-location-label").forEach((el) => {
        el.textContent = locationName;
      });
    }

    document.querySelectorAll("preferred-location").forEach((instance) => {
      if (instance === this) return;
      const otherCard = instance.querySelector(`[data-location-id="${CSS.escape(locationId)}"]`);
      if (otherCard && instance.cards) {
        instance.cards.forEach((c) => c.classList.remove("is-preferred"));
        otherCard.classList.add("is-preferred");
      }
    });

    document.dispatchEvent(
      new CustomEvent("store:location-changed", {
        detail: {
          locationId,
          locationName,
          available: true,
        },
      }),
    );

    if (syncToCart) {
      this.updateCartAttributes(locationId, locationName);
    }
  }
}

if (!customElements.get("preferred-location")) {
  customElements.define("preferred-location", PreferredLocation);
}
